import React, { useEffect, useState, useMemo } from 'react';
import { Settings, Clock, Cpu, FileJson, CheckCircle, XCircle, Timer, Play, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fetchConfig, fetchSchedulerStatus, runAnalyzer, runAllAnalyzers } from '../lib/api';
import { format, formatDistanceToNow } from 'date-fns';

const ANALYZER_DESCRIPTIONS: Record<string, { icon: string; color: string }> = {
  compliance: { icon: '🛡️', color: 'blue' },
  agent_health: { icon: '💚', color: 'green' },
  alert_aggregation: { icon: '🔔', color: 'red' },
  drift_detection: { icon: '📊', color: 'purple' },
  software_inventory: { icon: '📦', color: 'blue' },
  network_audit: { icon: '🌐', color: 'yellow' },
  patch_monitor: { icon: '🔧', color: 'green' },
  hardening_score: { icon: '🏋️', color: 'yellow' },
  cert_expiry: { icon: '📜', color: 'red' },
};

type TabId = 'scheduler' | 'analyzers' | 'config';

export default function ConfigPage() {
  const [config, setConfig] = useState<any>(null);
  const [scheduler, setScheduler] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('scheduler');
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      fetchConfig().catch(() => null),
      fetchSchedulerStatus().catch(() => []),
    ])
      .then(([cfg, sched]) => {
        setConfig(cfg);
        setScheduler(sched || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'scheduler', label: t('config.schedulerTab'), icon: <Clock className="w-4 h-4" /> },
    { id: 'analyzers', label: t('config.analyzersTab'), icon: <Cpu className="w-4 h-4" /> },
    { id: 'config', label: t('config.rawConfigTab'), icon: <FileJson className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'scheduler' && <SchedulerTab scheduler={scheduler} t={t} />}
      {activeTab === 'analyzers' && <AnalyzersTab scheduler={scheduler} t={t} />}
      {activeTab === 'config' && <RawConfigTab config={config} t={t} />}
    </div>
  );
}

function SchedulerTab({ scheduler, t }: { scheduler: any[]; t: (k: string) => string }) {
  if (scheduler.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
        {t('config.noJobs')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {scheduler.map((e: any, i: number) => {
        const hasRun = e.prev && new Date(e.prev).getFullYear() > 1;
        const meta = ANALYZER_DESCRIPTIONS[e.name] || { icon: '⚙️', color: 'blue' };
        const nextDate = e.next ? new Date(e.next) : null;
        const timeUntilNext = nextDate ? formatDistanceToNow(nextDate, { addSuffix: true }) : '';

        return (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{meta.icon}</span>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {e.name?.replace(/_/g, ' ')}
                </h4>
              </div>
              {hasRun ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-300 dark:text-gray-600" />
              )}
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('config.schedule')}</span>
                <code className="font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{e.schedule}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1"><Timer className="w-3 h-3" /> {t('config.nextRun')}</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {nextDate ? format(nextDate, 'HH:mm') : '-'}
                  {timeUntilNext && <span className="text-gray-400 dark:text-gray-500 ml-1">({timeUntilNext})</span>}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('config.lastRun')}</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {hasRun ? format(new Date(e.prev), 'MMM d, HH:mm') : t('config.never')}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AnalyzersTab({ scheduler, t }: { scheduler: any[]; t: (k: string) => string }) {
  const [runningAnalyzer, setRunningAnalyzer] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);
  const analyzerNames = [
    'compliance', 'agent_health', 'alert_aggregation', 'drift_detection',
    'software_inventory', 'network_audit', 'patch_monitor', 'hardening_score', 'cert_expiry',
  ];

  const schedulerMap = useMemo(() => {
    const map: Record<string, any> = {};
    scheduler.forEach((s) => { if (s.name) map[s.name] = s; });
    return map;
  }, [scheduler]);

  const handleRun = async (name: string) => {
    setRunningAnalyzer(name);
    try {
      await runAnalyzer(name);
      toast.success(t('config.analyzerTriggered'));
    } catch {
      toast.error(t('config.triggerFailed'));
    } finally {
      setRunningAnalyzer(null);
    }
  };

  const handleRunAll = async () => {
    setRunningAll(true);
    try {
      await runAllAnalyzers();
      toast.success(t('config.allAnalyzersTriggered'));
    } catch {
      toast.error(t('config.triggerFailed'));
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleRunAll}
          disabled={runningAll}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition-colors"
        >
          {runningAll ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <PlayCircle className="w-4 h-4" />
          )}
          {runningAll ? t('common.running') : t('config.runAll')}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {analyzerNames.map((name) => {
          const meta = ANALYZER_DESCRIPTIONS[name] || { icon: '⚙️', color: 'blue' };
          const sched = schedulerMap[name];
          const isScheduled = !!sched;
          const hasRun = sched?.prev && new Date(sched.prev).getFullYear() > 1;
          const isRunning = runningAnalyzer === name;

          return (
            <div key={name} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{meta.icon}</span>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
                    {name.replace(/_/g, ' ')}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t(`config.desc_${name}`)}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {isScheduled ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                    <CheckCircle className="w-3 h-3" /> {t('config.scheduled')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {t('config.notScheduled')}
                  </span>
                )}
                {hasRun && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {t('config.lastRun')}: {format(new Date(sched.prev), 'MMM d, HH:mm')}
                  </span>
                )}
                <button
                  onClick={() => handleRun(name)}
                  disabled={isRunning}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 transition-colors"
                >
                  {isRunning ? (
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                  {isRunning ? t('common.running') : t('config.runAnalyzer')}
                </button>
              </div>
              {isScheduled && (
                <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                  <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{sched.schedule}</code>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RawConfigTab({ config, t }: { config: any; t: (k: string) => string }) {
  if (!config) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 text-sm text-yellow-700 dark:text-yellow-400">
        {t('config.adminRequired')}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
        <Settings className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('config.configSanitized')}</h3>
      </div>
      <pre className="p-5 text-xs text-gray-600 dark:text-gray-300 overflow-auto max-h-[500px] bg-gray-50 dark:bg-gray-900/50 font-mono leading-relaxed">
        {JSON.stringify(config, null, 2)}
      </pre>
    </div>
  );
}
